// @ts-nocheck
import React, { FC, useEffect, useState, useRef, useMemo } from 'react'
import axios from 'axios'
import { Modal } from 'react-bootstrap'
import { PageLink, PageTitle, useLayout } from '../../../_payairo/layout/core'
import { KTIcon } from '../../../_payairo/helpers'
import { systemMode, useThemeMode } from '../../../_payairo/partials'
import { getAuth } from '../../modules/auth/core/AuthHelpers'
import ApexCharts, { ApexOptions } from 'apexcharts'
import { PieChart, Pie, Cell, Tooltip, ResponsiveContainer, LineChart, Line, AreaChart, Area, XAxis, YAxis, CartesianGrid, Legend } from 'recharts'
import Swal from 'sweetalert2'
import moment from 'moment'
import DashboardDateFilter, { DatePreset } from './DashboardDateFilter'
import {NotificationBell} from '../../components/NotificationBell'
import {getFcmToken} from '../../utils/fcmToken'
import { normalizeSupportTicketStatus } from '../../utils/ticketStatus'

interface TicketStats {
  total_tickets: number
  open_tickets: number
  closed_tickets: number
}

interface Counts {
  total_tickets: number
  open_tickets: number
  inprocess_tickets: number
  closed_tickets: number
  avg_monthly_tickets: number
  priority: {
    low: number
    medium: number
    high: number
  }
}

interface LatestTicket {
  id: number
  uuid?: string
  subject: string
  message?: string
  description?: string
  user_name: string
  user_email: string
  customer_name?: string
  status: string
  priority: string
  category?: string
  assigned_to?: string
  created_at?: string
}

interface MonthlyBreakdownItem {
  month: string
  year: number
  total: number
  open: number
  closed: number
  in_process?: number
  high_priority?: number
  resolution_rate?: number
}

const MONTH_LABELS: Record<number, string> = {
  1: 'Jan', 2: 'Feb', 3: 'Mar', 4: 'Apr', 5: 'May', 6: 'Jun',
  7: 'Jul', 8: 'Aug', 9: 'Sep', 10: 'Oct', 11: 'Nov', 12: 'Dec',
}

const MONTH_NAME_TO_NUM: Record<string, number> = {
  jan: 1,
  feb: 2,
  mar: 3,
  apr: 4,
  may: 5,
  jun: 6,
  jul: 7,
  aug: 8,
  sep: 9,
  oct: 10,
  nov: 11,
  dec: 12,
}

const CustomerSupportDashboard: FC = () => {
  const { showLoader, hideLoader } = useLayout()
  const { mode } = useThemeMode()
  const effectiveMode = mode === 'system' ? systemMode : mode
  const chartLabelColor = effectiveMode === 'dark' ? '#e5e7eb' : '#374151'
  const chartTitleColor = effectiveMode === 'dark' ? '#e5e7eb' : '#111827'
  const chartGridColor = effectiveMode === 'dark' ? '#334155' : '#e5e7eb'

  const [stats, setStats] = useState<TicketStats>({
    total_tickets: 0,
    open_tickets: 0,
    closed_tickets: 0,
  })
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [counts, setCounts] = useState<Counts | null>(null)
  const [allTickets, setAllTickets] = useState<LatestTicket[]>([])
  const [latestTickets, setLatestTickets] = useState<LatestTicket[]>([])
  const [monthlyBreakdown, setMonthlyBreakdown] = useState<MonthlyBreakdownItem[]>([])
  const [activePriorityName, setActivePriorityName] = useState<string | null>(null)
  const [ticketPage, setTicketPage] = useState(1)
  const TICKETS_PER_PAGE = 10

  // Request browser notification permission + generate FCM token once
  useEffect(() => {
    getFcmToken()
  }, [])

  const parseMonthToNumber = (m: unknown): number | null => {
    if (typeof m === 'number' && Number.isFinite(m) && m >= 1 && m <= 12) return m
    const s = String(m ?? '').trim()
    if (!s) return null
    const n = parseInt(s, 10)
    if (Number.isFinite(n) && n >= 1 && n <= 12) return n
    const key = s.slice(0, 3).toLowerCase()
    return MONTH_NAME_TO_NUM[key] ?? null
  }

  const monthlyLineData = useMemo(() => {
    const now = new Date()
    const axis: { year: number; month: number; label: string; key: string }[] = []
    for (let i = 11; i >= 0; i--) {
      const d = new Date(now.getFullYear(), now.getMonth() - i, 1)
      const month = d.getMonth() + 1
      const year = d.getFullYear()
      const label = `${MONTH_LABELS[month]} ${String(year).slice(-2)}`
      const key = `${year}-${String(month).padStart(2, '0')}`
      axis.push({ year, month, label, key })
    }

    const totals = new Map<string, number>()
    for (const item of monthlyBreakdown ?? []) {
      const year = Number(item?.year)
      const month = parseMonthToNumber(item?.month)
      if (!Number.isFinite(year) || !month) continue
      const key = `${year}-${String(month).padStart(2, '0')}`
      totals.set(key, Number(item?.total) || 0)
    }

    return axis.map((a) => ({
      name: a.label,
      total: totals.get(a.key) ?? 0,
    }))
  }, [monthlyBreakdown])

  // Filters
  const [search, setSearch] = useState('')
  const [debouncedSearch, setDebouncedSearch] = useState('')
  const [statusFilter, setStatusFilter] = useState<string>('all')
  const [priorityFilter, setPriorityFilter] = useState<string>('all')
  const [createdDateFilter, setCreatedDateFilter] = useState<string>('')
  const [viewingTicket, setViewingTicket] = useState<LatestTicket | null>(null)
  const [datePreset, setDatePreset] = useState<DatePreset>('last_year')
  const [customStart, setCustomStart] = useState('')
  const [customEnd, setCustomEnd] = useState('')

  const barChartRef = useRef<HTMLDivElement>(null)
  const barChartInstance = useRef<ApexCharts | null>(null)
  
  const statusDropdownRef = useRef<HTMLDivElement>(null)
  const priorityDropdownRef = useRef<HTMLDivElement>(null)
  const [isOpenStatus, setIsOpenStatus] = useState(false)
  const [isOpenPriority, setIsOpenPriority] = useState(false)

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (statusDropdownRef.current && !statusDropdownRef.current.contains(event.target as Node)) {
        setIsOpenStatus(false)
      }
      if (priorityDropdownRef.current && !priorityDropdownRef.current.contains(event.target as Node)) {
        setIsOpenPriority(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  useEffect(() => {
    fetchTickets()
    window.addEventListener('refreshDashboard', fetchTickets)
    return () => window.removeEventListener('refreshDashboard', fetchTickets)
  }, [datePreset, customStart, customEnd])

  const fetchTickets = async () => {
    try {
      setLoading(true)
      showLoader()
      setError(null)
      const auth = getAuth()
      if (!auth?.access_token) {
        setError('Authentication token is missing. Please login again.')
        setLoading(false)
        hideLoader()
        return
      }

      const params: Record<string, string> = { time_filter: datePreset }
      if (datePreset === 'custom') {
        if (customStart) params.start_date = customStart
        if (customEnd) params.end_date = customEnd
      }

      const response = await axios.get(
        'https://app.payairo.com/api/auth/customer-support/dashboard/',
        {
          headers: { Authorization: `Bearer ${auth.access_token}` },
          params,
        }
      )

      if (response.data?.status && response.data?.data) {
        const data = response.data.data
        if (data.counts) setCounts(data.counts)

        const monthlyTicketsData = data.analytics?.monthly_breakdown ?? data.monthly_breakdown ?? []
        if (Array.isArray(monthlyTicketsData)) {
          setMonthlyBreakdown(monthlyTicketsData)
        }

        const rawTickets: LatestTicket[] = Array.isArray(data.tickets) ? data.tickets : []
        const latest: LatestTicket[] = Array.isArray(data.latest_tickets) ? data.latest_tickets : rawTickets.slice(0, 8)

        setAllTickets(rawTickets)
        setLatestTickets(latest)

        setStats({
          total_tickets: data.counts?.total_tickets ?? 0,
          open_tickets: data.counts?.open_tickets ?? 0,
          closed_tickets: data.counts?.closed_tickets ?? 0,
        })
      } else {
        setError(response.data?.message || 'Failed to load dashboard data')
        setCounts(null)
        setAllTickets([])
        setLatestTickets([])
        setMonthlyBreakdown([])
      }
    } catch (err: any) {
      console.error('Error fetching tickets:', err)
      setError(err?.response?.data?.message || err?.message || 'Failed to load dashboard data.')
      setCounts(null)
      setAllTickets([])
      setLatestTickets([])
      setMonthlyBreakdown([])
    } finally {
      setLoading(false)
      hideLoader()
    }
  }

  const handlePriorityChange = async (id: number, newPriority: string) => {
    try {
      const auth = getAuth()
      await axios.patch(
        `https://app.payairo.com/api/auth/ticket/${id}/priority/`,
        { priority: newPriority },
        { headers: { Authorization: `Bearer ${auth?.access_token}` } }
      )

      setAllTickets((prev) =>
        prev.map((t) => (t.id === id ? { ...t, priority: newPriority } : t))
      )
      setLatestTickets((prev) =>
        prev.map((t) => (t.id === id ? { ...t, priority: newPriority } : t))
      )

      window.dispatchEvent(new Event('refreshDashboard'))

      Swal.fire({
        text: 'Priority updated successfully',
        icon: 'success',
        timer: 1500,
        showConfirmButton: false,
      })
    } catch (err) {
      console.error('Error updating priority:', err)
      Swal.fire({
        text: 'Failed to update priority',
        icon: 'error',
        confirmButtonText: 'Ok',
      })
    }
  }

  const handleStatusChange = async (id: number, newStatus: string) => {
    try {
      const auth = getAuth()
      await axios.patch(
        `https://app.payairo.com/api/auth/ticket/${id}/status/`,
        { status: newStatus },
        { headers: { Authorization: `Bearer ${auth?.access_token}` } }
      )

      setAllTickets((prev) =>
        prev.map((t) => (t.id === id ? { ...t, status: newStatus } : t))
      )
      setLatestTickets((prev) =>
        prev.map((t) => (t.id === id ? { ...t, status: newStatus } : t))
      )

      window.dispatchEvent(new Event('refreshDashboard'))

      Swal.fire({
        text: 'Status updated successfully',
        icon: 'success',
        timer: 1500,
        showConfirmButton: false,
      })
    } catch (err) {
      console.error('Error updating status:', err)
      Swal.fire({
        text: 'Failed to update status',
        icon: 'error',
        confirmButtonText: 'Ok',
      })
    }
  }

  // Derived helpers
  const normalizeStatus = (value?: string) => String(value || '').toLowerCase()
  const normalizePriority = (value?: string) => String(value || '').toLowerCase()

  const filteredTickets = useMemo(() => {
    let items = allTickets.length ? allTickets : latestTickets
    if (!items.length) return []

    if (debouncedSearch.trim()) {
      const q = debouncedSearch.trim().toLowerCase()
      items = items.filter((t) => {
        return (
          String(t.id).includes(q) ||
          String(t.uuid || '')
            .toLowerCase()
            .includes(q) ||
          (t.user_email || '').toLowerCase().includes(q) ||
          (t.subject || '').toLowerCase().includes(q)
        )
      })
    }

    if (statusFilter !== 'all') {
      items = items.filter(
        (t) => normalizeSupportTicketStatus(t.status) === normalizeSupportTicketStatus(statusFilter)
      )
    }

    if (priorityFilter !== 'all') {
      items = items.filter((t) => normalizePriority(t.priority) === priorityFilter)
    }

    if (createdDateFilter) {
      items = items.filter((t) => {
        if (!t.created_at) return false
        try {
          const d = new Date(t.created_at)
          const iso = d.toISOString().slice(0, 10)
          return iso === createdDateFilter
        } catch {
          return false
        }
      })
    }

    return items
  }, [allTickets, latestTickets, debouncedSearch, statusFilter, priorityFilter, createdDateFilter])

  // Debounce search input (300ms) to reduce re-renders on large tables
  useEffect(() => {
    const t = window.setTimeout(() => setDebouncedSearch(search), 300)
    return () => window.clearTimeout(t)
  }, [search])

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Ignore if typing in an input/textarea
      if (document.activeElement?.tagName === 'INPUT' || document.activeElement?.tagName === 'TEXTAREA') return

      const totalFiltered = filteredTickets.length
      const totalPages = Math.max(1, Math.ceil(totalFiltered / TICKETS_PER_PAGE))
      
      if (e.key === 'ArrowRight') {
        setTicketPage((prev) => Math.min(totalPages, prev + 1))
      } else if (e.key === 'ArrowLeft') {
        setTicketPage((prev) => Math.max(1, prev - 1))
      }
    }
    
    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [filteredTickets.length])

  // ApexCharts - Tickets by Status (Simple Bar)
  useEffect(() => {
    if (!barChartRef.current) return
    const statusData = [
      counts?.open_tickets ?? 0,
      counts?.inprocess_tickets ?? 0,
      counts?.closed_tickets ?? 0,
    ]
    const opts: ApexOptions = {
      series: [{ name: 'Tickets', data: statusData }],
      chart: { type: 'bar', height: 350, toolbar: { show: false } },
      plotOptions: { bar: { borderRadius: 4, distributed: true, columnWidth: '50%' } },
      colors: ['#3b82f6', '#f59e0b', '#10b981'],
      xaxis: { categories: ['Open', 'In Process', 'Closed'], labels: { style: { colors: chartLabelColor } } },
      yaxis: { labels: { style: { colors: chartLabelColor } } },
      grid: { borderColor: chartGridColor },
      legend: { show: false }
    }
    const ch = new ApexCharts(barChartRef.current, opts)
    ch.render()
    barChartInstance.current = ch
    return () => ch.destroy()
  }, [counts, effectiveMode])

  return (
    <div className='cs-dashboard'>
      <div className='cs-dashboard-topbar'>
        <div className='cs-topbar-left'>
          <div className='cs-topbar-title-wrap'>
            <h2 className='cs-topbar-title'>Customer Support</h2>
            <span className='cs-topbar-subtitle'>Monitor tickets, workload and activity</span>
          </div>
        </div>
        <div className='cs-topbar-right' style={{gap: '12px'}}>
          <DashboardDateFilter
            preset={datePreset}
            onPresetChange={setDatePreset}
            customStart={customStart}
            customEnd={customEnd}
            onCustomChange={(start, end) => {
              setCustomStart(start)
              setCustomEnd(end)
            }}
          />
          <NotificationBell />
        </div>
      </div>

      <div className='cs-dashboard-content'>
        {error && (
          <div className='cs-alert cs-alert-danger'>
            <KTIcon iconType='solid' iconName='information' className='fs-2hx me-3' />
            <span>{error}</span>
          </div>
        )}

        <div className='cs-kpi-grid'>
          <div className='cs-kpi-card cs-kpi-card-hover'>
            <div className='cs-kpi-header'>
              <div className='cs-kpi-icon cs-kpi-icon-lg cs-kpi-icon-diamond cs-kpi-icon-blue'>
                <div className='cs-kpi-icon-inner'>
                  <KTIcon iconType='solid' iconName='abstract-26' className='fs-3x text-white' />
                </div>
              </div>
              <span className='cs-kpi-indicator cs-kpi-indicator-blue'>All</span>
            </div>
            <div className='cs-kpi-body'>
              <span className='cs-kpi-label'>Total Tickets</span>
              <span className='cs-kpi-value'>{loading ? '—' : (counts?.total_tickets ?? 0)}</span>
            </div>
          </div>
          <div className='cs-kpi-card cs-kpi-card-hover'>
            <div className='cs-kpi-header'>
              <div className='cs-kpi-icon cs-kpi-icon-lg cs-kpi-icon-diamond cs-kpi-icon-indigo'>
                <div className='cs-kpi-icon-inner'>
                  <KTIcon iconType='solid' iconName='message-text' className='fs-3x text-white' />
                </div>
              </div>
              <span className='cs-kpi-indicator cs-kpi-indicator-sky'>Open</span>
            </div>
            <div className='cs-kpi-body'>
              <span className='cs-kpi-label'>Open Tickets</span>
              <span className='cs-kpi-value'>{loading ? '—' : (counts?.open_tickets ?? 0)}</span>
            </div>
          </div>
          <div className='cs-kpi-card cs-kpi-card-hover'>
            <div className='cs-kpi-header'>
              <div className='cs-kpi-icon cs-kpi-icon-lg cs-kpi-icon-diamond cs-kpi-icon-amber'>
                <div className='cs-kpi-icon-inner'>
                  <KTIcon iconType='solid' iconName='time' className='fs-3x text-white' />
                </div>
              </div>
              <span className='cs-kpi-indicator cs-kpi-indicator-amber'>In Progress</span>
            </div>
            <div className='cs-kpi-body'>
              <span className='cs-kpi-label'>In Process</span>
              <span className='cs-kpi-value'>{loading ? '—' : (counts?.inprocess_tickets ?? 0)}</span>
            </div>
          </div>
          <div className='cs-kpi-card cs-kpi-card-hover'>
            <div className='cs-kpi-header'>
              <div className='cs-kpi-icon cs-kpi-icon-lg cs-kpi-icon-diamond cs-kpi-icon-yellow'>
                <div className='cs-kpi-icon-inner'>
                  <KTIcon iconType='solid' iconName='arrow-right' className='fs-3x text-white' />
                </div>
              </div>
              <span className='cs-kpi-indicator cs-kpi-indicator-yellow'>Waiting</span>
            </div>
            <div className='cs-kpi-body'>
              <span className='cs-kpi-label'>Avg Monthly Tickets</span>
              <span className='cs-kpi-value'>{loading ? '—' : (counts?.avg_monthly_tickets ?? 0)}</span>
            </div>
          </div>
          <div className='cs-kpi-card cs-kpi-card-hover'>
            <div className='cs-kpi-header'>
              <div className='cs-kpi-icon cs-kpi-icon-lg cs-kpi-icon-diamond cs-kpi-icon-green'>
                <div className='cs-kpi-icon-inner'>
                  <KTIcon iconType='solid' iconName='check-circle' className='fs-3x text-white' />
                </div>
              </div>
              <span className='cs-kpi-indicator cs-kpi-indicator-emerald'>Closed</span>
            </div>
            <div className='cs-kpi-body'>
              <span className='cs-kpi-label'>Closed Tickets</span>
              <span className='cs-kpi-value'>{loading ? '—' : (counts?.closed_tickets ?? 0)}</span>
            </div>
          </div>
          <div className='cs-kpi-card cs-kpi-card-hover'>
            <div className='cs-kpi-header'>
              <div className='cs-kpi-icon cs-kpi-icon-lg cs-kpi-icon-diamond cs-kpi-icon-red'>
                <div className='cs-kpi-icon-inner'>
                  <KTIcon iconType='solid' iconName='flash-circle' className='fs-3x text-white' />
                </div>
              </div>
              <span className='cs-kpi-indicator cs-kpi-indicator-rose'>High</span>
            </div>
            <div className='cs-kpi-body'>
              <span className='cs-kpi-label'>High Priority</span>
              <span className='cs-kpi-value'>{loading ? '—' : (counts?.priority?.high ?? 0)}</span>
            </div>
          </div>
        </div>

        <div className='cs-section'>
          <div className='cs-charts-row'>
            <div className='cs-card cs-chart-card'>
              <div className='cs-card-header'>
                <h3 className='cs-card-title'>Tickets by Status</h3>
                <span className='cs-card-subtitle'>Open, in process, closed</span>
              </div>
              <div ref={barChartRef} className='cs-chart' />
            </div>

            <div className='cs-card cs-chart-card'>
              <div className='cs-card-header'>
                <h3 className='cs-card-title'>Monthly Tickets</h3>
                <span className='cs-card-subtitle'>Ticket activity trends</span>
              </div>
              <div style={{ height: '350px', width: '100%', marginTop: '20px' }}>
                <ResponsiveContainer width='100%' height='100%'>
                  {/* @ts-ignore */}
                  <LineChart data={monthlyLineData} margin={{ top: 20, right: 30, left: -10, bottom: 10 }}>
                    <CartesianGrid vertical={false} strokeDasharray='3 3' stroke={effectiveMode === 'dark' ? 'rgba(255,255,255,0.05)' : '#e5e7eb'} />
                    <XAxis
                      dataKey='name'
                      axisLine={false}
                      tickLine={false}
                      tick={{ fill: chartLabelColor, fontSize: 12, fontWeight: '500' }}
                      dy={10}
                    />
                    <YAxis axisLine={false} tickLine={false} tick={{ fill: chartLabelColor, fontSize: 12 }} />
                    <Tooltip
                      contentStyle={{
                        backgroundColor: effectiveMode === 'dark' ? '#1e293b' : '#ffffff',
                        border: effectiveMode === 'dark' ? '1px solid #334155' : '1px solid #e5e7eb',
                        borderRadius: '12px',
                        boxShadow: effectiveMode === 'dark' ? '0 20px 25px -5px rgba(0, 0, 0, 0.4)' : '0 10px 15px -3px rgba(0, 0, 0, 0.1)',
                        padding: '12px',
                      }}
                      itemStyle={{ fontSize: '13px', fontWeight: '700', color: '#3b82f6' }}
                      labelStyle={{ color: effectiveMode === 'dark' ? '#fff' : '#111827', marginBottom: '4px', fontWeight: '600' }}
                      cursor={{ stroke: 'rgba(59, 130, 246, 0.2)', strokeWidth: 2 }}
                      formatter={(value: any) => [`Tickets: ${value}`, '']}
                    />
                    <Line
                      type='monotone'
                      dataKey='total'
                      name='Tickets'
                      stroke='#3b82f6'
                      strokeWidth={4}
                      dot={{ r: 6, fill: '#3b82f6', strokeWidth: 2, stroke: '#1e293b' }}
                      activeDot={{ r: 8, fill: '#60a5fa', strokeWidth: 2, stroke: '#fff' }}
                      isAnimationActive={true}
                      animationDuration={1500}
                    />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </div>
          </div>
        </div>

        <div className='cs-section'>
          <div className='cs-charts-row'>
            <div className='cs-card cs-chart-card cs-equal-height-card'>
              <div className='cs-card-header'>
                <h3 className='cs-card-title'>Tickets by Priority</h3>
                <span className='cs-card-subtitle'>Urgency distribution</span>
              </div>
              <div style={{ width: '100%' }}>
                {(() => {
                  const pieData = [
                    { name: 'Low', value: counts?.priority?.low || 0 },
                    { name: 'Medium', value: counts?.priority?.medium || 0 },
                    { name: 'High', value: counts?.priority?.high || 0 },
                  ]
                  const totalPriority = pieData.reduce((acc, curr) => acc + curr.value, 0)
                  const COLORS = ['#10b981', '#f59e0b', '#ef4444']

                  return (
                    <>
                      <div style={{ height: 350, width: '100%', position: 'relative' }}>
                        <ResponsiveContainer width='100%' height='100%'>
                          {/* @ts-ignore */}
                          <PieChart>
                            {/* @ts-ignore */}
                            <Pie
                              data={pieData}
                              cx='50%'
                              cy='50%'
                              innerRadius={80}
                              outerRadius={110}
                              paddingAngle={5}
                              dataKey='value'
                              label={false}
                              onMouseLeave={() => setActivePriorityName(null)}
                              onMouseEnter={(_, idx) => {
                                const item = pieData?.[idx]
                                setActivePriorityName(item?.name ?? null)
                              }}
                            >
                              {pieData.map((entry, index) => (
                                <Cell
                                  key={`cell-${index}`}
                                  fill={COLORS[index % COLORS.length]}
                                  fillOpacity={
                                    !activePriorityName || activePriorityName === entry.name ? 1 : 0.35
                                  }
                                />
                              ))}
                            </Pie>
                          </PieChart>
                        </ResponsiveContainer>
                        {(() => {
                          const active = pieData.find(p => p.name === activePriorityName)
                          const activeColor = activePriorityName === 'Low' ? '#10b981' : activePriorityName === 'Medium' ? '#f59e0b' : '#ef4444'
                          return (
                            <div
                              style={{
                                position: 'absolute',
                                top: '50%',
                                left: '50%',
                                transform: 'translate(-50%, -50%)',
                                textAlign: 'center',
                                pointerEvents: 'none',
                                backgroundColor: effectiveMode === 'dark' ? '#111827' : '#ffffff',
                                borderRadius: '50%',
                                width: '120px',
                                height: '120px',
                                display: 'flex',
                                flexDirection: 'column',
                                justifyContent: 'center',
                                alignItems: 'center',
                                border: active ? `2px solid ${activeColor}` : (effectiveMode === 'dark' ? '2px solid #334155' : '2px solid #e5e7eb'),
                                boxShadow: active ? `0 0 16px ${activeColor}55` : (effectiveMode === 'dark' ? '0 4px 12px rgba(0,0,0,0.5)' : '0 4px 12px rgba(0,0,0,0.05)'),
                                transition: 'all 0.25s ease',
                              }}
                            >
                              <div style={{ fontSize: active ? '28px' : '36px', fontWeight: '800', color: active ? activeColor : (effectiveMode === 'dark' ? '#ffffff' : '#111827'), transition: 'all 0.2s ease' }}>
                                {active ? active.value : totalPriority}
                              </div>
                              <div
                                style={{
                                  fontSize: '11px',
                                  color: active ? activeColor : (effectiveMode === 'dark' ? '#94a3b8' : '#64748b'),
                                  textTransform: 'uppercase',
                                  fontWeight: '700',
                                  transition: 'all 0.2s ease',
                                }}
                              >
                                {active ? active.name : 'Total'}
                              </div>
                            </div>
                          )
                        })()}
                      </div>
                      <style>{`
                        @keyframes tooltipFadeIn {
                          from { opacity: 0; transform: translateY(4px); }
                          to   { opacity: 1; transform: translateY(0); }
                        }
                        .cs-priority-legend-card {
                          display: flex;
                          align-items: center;
                          justify-content: space-between;
                          gap: 10px;
                          padding: 10px 12px;
                          border-radius: 12px;
                          background: var(--cs-input-bg);
                          border: 1px solid var(--cs-border);
                          cursor: pointer;
                          transition: all 0.2s ease;
                          position: relative;
                        }
                        .cs-priority-legend-card:hover {
                          transform: scale(1.05);
                        }
                        .cs-priority-legend-card.low:hover  { box-shadow: 0 0 12px rgba(34,197,94,0.5);  background: rgba(16,185,129,0.1); border-color: rgba(16,185,129,0.35); }
                        .cs-priority-legend-card.medium:hover { box-shadow: 0 0 12px rgba(245,158,11,0.5); background: rgba(245,158,11,0.1); border-color: rgba(245,158,11,0.35); }
                        .cs-priority-legend-card.high:hover { box-shadow: 0 0 12px rgba(239,68,68,0.5);  background: rgba(239,68,68,0.1);  border-color: rgba(239,68,68,0.35); }
                        .cs-priority-tooltip {
                          position: absolute;
                          bottom: calc(100% + 10px);
                          left: 50%;
                          transform: translateX(-50%);
                          z-index: 9999;
                          background: #1a1f2e;
                          border: 1px solid rgba(255,255,255,0.12);
                          border-radius: 10px;
                          padding: 12px 16px;
                          box-shadow: 0 8px 24px rgba(0,0,0,0.5);
                          width: 200px;
                          animation: tooltipFadeIn 0.18s ease both;
                          pointer-events: none;
                        }
                        .cs-priority-tooltip::after {
                          content: '';
                          position: absolute;
                          top: 100%;
                          left: 50%;
                          transform: translateX(-50%);
                          border: 6px solid transparent;
                          border-top-color: #1a1f2e;
                        }
                      `}</style>
                      <div
                        style={{
                          display: 'flex',
                          flexWrap: 'wrap',
                          alignItems: 'center',
                          justifyContent: 'center',
                          gap: '8px',
                          marginTop: '12px',
                          padding: '0 12px 2px',
                          overflow: 'visible',
                          position: 'relative',
                        }}
                      >
                        {[
                          {
                            name: 'Low',
                            cls: 'low',
                            color: '#10b981',
                            emoji: '🟢',
                            // message: 'Normal urgency — can be scheduled',
                          },
                          {
                            name: 'Medium',
                            cls: 'medium',
                            color: '#f59e0b',
                            emoji: '🟡',
                            // message: 'Moderate urgency — address soon',
                          },
                          {
                            name: 'High',
                            cls: 'high',
                            color: '#ef4444',
                            emoji: '🔴',
                            // message: 'Critical urgency — resolve immediately',
                          },
                        ].map((meta, idx) => {
                          const p = pieData[idx]
                          const pct = totalPriority > 0 ? Math.round((p.value / totalPriority) * 100) : 0
                          const isHovered = activePriorityName === meta.name
                          return (
                            <div
                              key={meta.name}
                              className={`cs-priority-legend-card ${meta.cls}`}
                              onMouseEnter={() => setActivePriorityName(meta.name)}
                              onMouseLeave={() => setActivePriorityName(null)}
                              style={{ flex: '1 1 auto', minWidth: '80px', padding: '8px 10px' }}
                            >
                              {/* Rich Tooltip */}
                              {isHovered && (
                                <div
                                  className='cs-priority-tooltip'
                                  style={{
                                    left: idx === 2 ? 'auto' : idx === 0 ? '0' : '50%',
                                    right: idx === 2 ? '0' : 'auto',
                                    transform: idx === 1 ? 'translateX(-50%)' : 'none',
                                  }}
                                >
                                  <div style={{ fontSize: '13px', fontWeight: 600, color: '#fff', marginBottom: '8px' }}>
                                    {meta.emoji} {meta.name} Priority Tickets
                                  </div>
                                  <div style={{ height: '1px', background: 'rgba(255,255,255,0.1)', marginBottom: '8px' }} />
                                  <div style={{ display: 'flex', alignItems: 'baseline', gap: '6px' }}>
                                    <span style={{ fontSize: '20px', fontWeight: 800, color: '#fff' }}>{p.value}</span>
                                    <span style={{ fontSize: '11px', color: '#94a3b8' }}>{p.value === 1 ? 'ticket' : 'tickets'}</span>
                                    <span style={{ fontSize: '12px', fontWeight: 700, color: meta.color, marginLeft: 'auto' }}>{pct}%</span>
                                  </div>
                                </div>
                              )}
                              {/* Card Content */}
                              <div style={{ display: 'flex', alignItems: 'center', gap: '6px', minWidth: 0, whiteSpace: 'nowrap' }}>
                                <span
                                  style={{
                                    width: '8px',
                                    height: '8px',
                                    borderRadius: '50%',
                                    background: COLORS[idx % COLORS.length],
                                    flex: '0 0 auto',
                                  }}
                                />
                                <span style={{ fontSize: '11px', fontWeight: 700, color: effectiveMode === 'dark' ? '#e5e7eb' : '#374151', textTransform: 'capitalize' }}>
                                  {meta.name}
                                </span>
                              </div>
                              <div style={{ display: 'flex', alignItems: 'baseline', gap: '6px', flex: '0 0 auto' }}>
                                <span style={{ fontSize: '14px', fontWeight: 800, color: effectiveMode === 'dark' ? '#ffffff' : '#111827' }}>{p.value}</span>
                                <span style={{ fontSize: '11px', color: effectiveMode === 'dark' ? '#94a3b8' : '#64748b' }}>{pct}%</span>
                              </div>
                            </div>
                          )
                        })}
                      </div>
                    </>
                  )
                })()}
              </div>
            </div>

            <div className='cs-card cs-assigned-card cs-equal-height-card'>
              <div className='cs-card-header'>
                <h3 className='cs-card-title'>Latest Tickets</h3>
                <span className='cs-card-subtitle'>Recent support requests</span>
              </div>
              <div className='cs-table-wrapper cs-latest-table-wrap overflow-x-auto'>
                <table className='cs-table-modern'>
                  <thead>
                    <tr>
                      <th style={{ width: '70px', padding: '0.75rem 1rem' }}>ID</th>
                      <th style={{ padding: '0.75rem 1rem' }}>Ticket Info</th>
                      <th style={{ padding: '0.75rem 1rem' }}>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {latestTickets.slice(0, 5).map((t) => {
                      const status = normalizeStatus(t.status)
                      const statusClass = status === 'open' ? 'cs-pill-status-open' : (status === 'closed' || status === 'resolved') ? 'cs-pill-status-closed' : 'cs-pill-status-progress'

                      return (
                        <tr key={t.id} onClick={() => setViewingTicket(t)}>
                          <td style={{ padding: '0.75rem 1rem' }}>
                            <span className='fw-bold text-gray-600 fs-7'>#{t.id}</span>
                          </td>
                          <td style={{ padding: '0.75rem 1rem' }}>
                            <div className='d-flex flex-column'>
                              <span
                                className='fw-bold text-gray-800 fs-7 truncate'
                                style={{ maxWidth: '140px' }}
                                title={t.subject}
                              >
                                {t.subject}
                              </span>
                              <span className='text-muted fs-8' title={t.user_email}>
                                {t.user_email || '—'}
                              </span>
                            </div>
                          </td>
                          <td style={{ padding: '0.75rem 1rem' }}>
                            <span
                              className={`cs-pill-badge ${statusClass}`}
                              style={{ minWidth: '80px', padding: '0.2rem 0.5rem', fontSize: '10px' }}
                            >
                              {t.status}
                            </span>
                          </td>
                        </tr>
                      )
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>

        <div className='cs-section'>
          <div className='cs-card cs-table-card'>
            <div className='cs-card-header cs-table-header'>
              <div>
                <h3 className='cs-card-title'>All Tickets</h3>
                <span className='cs-card-subtitle'>Filter and manage tickets</span>
              </div>
              <div className='cs-table-filters'>
                <div className='cs-filter-group cs-filter-search'>
                  {/* <KTIcon iconName='magnifier' className='cs-filter-search-icon' /> */}
                  <input type='text' className='cs-input' placeholder='Search by ticket ID, email, subject…' value={search} onChange={(e) => { setSearch(e.target.value); setTicketPage(1); }} />
                </div>
                <div className='cs-filter-group' ref={statusDropdownRef} style={{ position: 'relative' }}>
                  <button 
                    className='cs-select' 
                    onClick={() => setIsOpenStatus(!isOpenStatus)}
                    style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '8px', width: '100%', minWidth: '140px' }}
                  >
                    <span className='truncate'>
                      {statusFilter === 'all' 
                        ? 'All Status' 
                        : statusFilter === 'inprocess' 
                        ? 'In Process' 
                        : statusFilter.charAt(0).toUpperCase() + statusFilter.slice(1)}
                    </span>
                    <KTIcon iconName={isOpenStatus ? 'up' : 'down'} className='fs-5' />
                  </button>
                  {isOpenStatus && (
                    <div className='cs-custom-dropdown-menu'>
                      {['all', 'open', 'inprocess', 'closed'].map((val) => (
                        <div 
                          key={val}
                          className={`cs-custom-dropdown-item ${statusFilter === val ? 'active' : ''}`}
                          onClick={() => { setStatusFilter(val); setTicketPage(1); setIsOpenStatus(false); }}
                        >
                          {val === 'all' 
                            ? 'All Status' 
                            : val === 'inprocess' 
                            ? 'In Process' 
                            : val.charAt(0).toUpperCase() + val.slice(1)}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
                <div className='cs-filter-group' ref={priorityDropdownRef} style={{ position: 'relative' }}>
                  <button 
                    className='cs-select' 
                    onClick={() => setIsOpenPriority(!isOpenPriority)}
                    style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '8px', width: '100%', minWidth: '140px' }}
                  >
                    <span className='truncate'>{priorityFilter === 'all' ? 'All Priority' : priorityFilter.charAt(0).toUpperCase() + priorityFilter.slice(1)}</span>
                    <KTIcon iconName={isOpenPriority ? 'up' : 'down'} className='fs-5' />
                  </button>
                  {isOpenPriority && (
                    <div className='cs-custom-dropdown-menu'>
                      {['all', 'low', 'medium', 'high'].map((val) => (
                        <div 
                          key={val}
                          className={`cs-custom-dropdown-item ${priorityFilter === val ? 'active' : ''}`}
                          onClick={() => { setPriorityFilter(val); setTicketPage(1); setIsOpenPriority(false); }}
                        >
                          {val === 'all' ? 'All Priority' : val.charAt(0).toUpperCase() + val.slice(1)}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>
            <div className='cs-table-wrapper overflow-x-auto'>
              <table className='cs-table-modern'>
                <thead>
                  <tr>
                    <th>ID</th>
                    <th>Subject</th>
                    <th>User email</th>
                    <th>Priority</th>
                    <th>Status</th>
                    <th>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {(() => {
                    const totalFiltered = filteredTickets.length
                    const totalPages = Math.max(1, Math.ceil(totalFiltered / TICKETS_PER_PAGE))
                    const safeCurrentPage = Math.min(ticketPage, totalPages)
                    const startIdx = (safeCurrentPage - 1) * TICKETS_PER_PAGE
                    const paginatedTickets = filteredTickets.slice(startIdx, startIdx + TICKETS_PER_PAGE)

                    return paginatedTickets.map((t) => {
                      const priority = normalizePriority(t.priority)
                      const status = normalizeStatus(t.status)

                      const priorityClass = priority === 'low' ? 'cs-pill-priority-low' : priority === 'high' ? 'cs-pill-priority-high' : 'cs-pill-priority-medium'
                      const statusClass = status === 'open' ? 'cs-pill-status-open' : (status === 'closed' || status === 'resolved') ? 'cs-pill-status-closed' : 'cs-pill-status-progress'

                      return (
                        <tr key={t.id} onClick={() => setViewingTicket(t)}>
                          <td>
                            <span className='cs-ticket-id fw-bold'>#{t.id}</span>
                          </td>
                          <td className='cs-ticket-subject max-w-[250px] truncate' title={t.subject}>
                            {t.subject}
                          </td>
                          <td className='cs-ticket-customer fw-medium' title={t.user_email}>
                            {t.user_email || '—'}
                          </td>
                          <td>
                            <span className={`cs-pill-badge ${priorityClass}`}>
                              {t.priority}
                            </span>
                          </td>
                          <td>
                            <span className={`cs-pill-badge ${statusClass}`}>
                              {t.status}
                            </span>
                          </td>
                          <td>
                            <button
                              className='cs-btn-view-modern'
                              onClick={(e) => {
                                e.stopPropagation()
                                setViewingTicket(t)
                              }}
                            >
                              <KTIcon iconName='eye' className='fs-6' />
                              <span>View</span>
                            </button>
                          </td>
                        </tr>
                      )
                    })
                  })()}
                </tbody>
              </table>

              {/* Pagination UI */}
              {(() => {
                const totalFiltered = filteredTickets.length
                const totalPages = Math.max(1, Math.ceil(totalFiltered / TICKETS_PER_PAGE))
                const safeCurrentPage = Math.min(ticketPage, totalPages)
                const startIdx = (safeCurrentPage - 1) * TICKETS_PER_PAGE
                const showingFrom = totalFiltered === 0 ? 0 : startIdx + 1
                const showingTo = Math.min(startIdx + TICKETS_PER_PAGE, totalFiltered)

                const getPageNumbers = (): (number | '...')[] => {
                  if (totalPages <= 7) return Array.from({ length: totalPages }, (_, i) => i + 1)
                  const pages: (number | '...')[] = []
                  const delta = 2
                  const left = Math.max(2, safeCurrentPage - delta)
                  const right = Math.min(totalPages - 1, safeCurrentPage + delta)
                  pages.push(1)
                  if (left > 2) pages.push('...')
                  for (let i = left; i <= right; i++) pages.push(i)
                  if (right < totalPages - 1) pages.push('...')
                  pages.push(totalPages)
                  return pages
                }

                const navBtn = (label: string, disabled: boolean, onClick: () => void): React.ReactNode => (
                  <button
                    disabled={disabled}
                    onClick={onClick}
                    style={{
                      padding: '6px 12px',
                      borderRadius: '6px',
                      border: effectiveMode === 'dark' ? '1px solid rgba(255,255,255,0.08)' : '1px solid #e5e7eb',
                      cursor: disabled ? 'not-allowed' : 'pointer',
                      fontSize: '15px',
                      fontWeight: 500,
                      transition: 'all 0.2s ease',
                      background: 'transparent',
                      color: disabled ? (effectiveMode === 'dark' ? '#475569' : '#94a3b8') : (effectiveMode === 'dark' ? '#94a3b8' : '#64748b'),
                      opacity: disabled ? 0.4 : 1,
                    }}
                    onMouseEnter={(e) => { if (!disabled) { e.currentTarget.style.color = effectiveMode === 'dark' ? '#fff' : '#111827'; e.currentTarget.style.background = effectiveMode === 'dark' ? 'rgba(255,255,255,0.08)' : '#f1f5f9'; } }}
                    onMouseLeave={(e) => { if (!disabled) { e.currentTarget.style.color = effectiveMode === 'dark' ? '#94a3b8' : '#64748b'; e.currentTarget.style.background = 'transparent'; } }}
                  >
                    {label}
                  </button>
                )

                if (totalFiltered === 0) return null

                return (
                  <div
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      marginTop: '16px',
                      paddingTop: '16px',
                      borderTop: '1px solid rgba(255,255,255,0.08)',
                      flexWrap: 'wrap',
                      gap: '10px',
                    }}
                  >
                    {/* Left: showing text */}
                    <span style={{ fontSize: '15px', color: effectiveMode === 'dark' ? '#94a3b8' : '#64748b' }}>
                      Showing <strong style={{ color: effectiveMode === 'dark' ? '#e5e7eb' : '#111827' }}>{showingFrom}–{showingTo}</strong> of{' '}
                      <strong style={{ color: effectiveMode === 'dark' ? '#e5e7eb' : '#111827' }}>{totalFiltered}</strong> tickets
                    </span>

                    {/* Right: page controls */}
                    <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                      {navBtn('← Prev', safeCurrentPage === 1, () => setTicketPage((p) => Math.max(1, p - 1)))}

                      {getPageNumbers().map((pg, idx) =>
                        pg === '...' ? (
                          <span key={`ellipsis-${idx}`} style={{ padding: '6px 4px', color: '#475569', fontSize: '15px' }}>…</span>
                        ) : (
                          <button
                            key={pg}
                            onClick={() => setTicketPage(pg as number)}
                            style={{
                              padding: '6px 12px',
                              borderRadius: '6px',
                              border: 'none',
                              cursor: 'pointer',
                              fontSize: '15px',
                              fontWeight: 500,
                              transition: 'all 0.2s ease',
                              background: pg === safeCurrentPage ? '#3b4fd8' : 'transparent',
                              color: pg === safeCurrentPage ? '#ffffff' : (effectiveMode === 'dark' ? '#94a3b8' : '#64748b'),
                            }}
                            onMouseEnter={(e) => { if (pg !== safeCurrentPage) e.currentTarget.style.background = effectiveMode === 'dark' ? 'rgba(255,255,255,0.08)' : '#f1f5f9' }}
                            onMouseLeave={(e) => { if (pg !== safeCurrentPage) e.currentTarget.style.background = 'transparent' }}
                          >
                            {pg}
                          </button>
                        )
                      )}

                      {navBtn('Next →', safeCurrentPage === totalPages, () => setTicketPage((p) => Math.min(totalPages, p + 1)))}
                    </div>
                  </div>
                )
              })()}
            </div>

          </div>
        </div>
      </div>

      <Modal show={!!viewingTicket} onHide={() => setViewingTicket(null)} centered contentClassName='cs-ticket-modal-content'>
        <div className='cs-ticket-modal-header'>
          <h3 className='cs-ticket-modal-title'>Ticket #{viewingTicket?.id}</h3>
          <button className='cs-ticket-modal-close' onClick={() => setViewingTicket(null)}>
            <KTIcon iconName='cross' className='fs-2' />
          </button>
        </div>
        <div className='cs-ticket-modal-body'>
          <div className='cs-ticket-meta'>
            <div className='cs-ticket-meta-row'>
              <span className='cs-ticket-meta-label'>User email</span>
              <span className='cs-ticket-meta-value'>{viewingTicket?.user_email || '—'}</span>
            </div>
            <div className='cs-ticket-meta-row'>
              <span className='cs-ticket-meta-label'>Date</span>
              <span className='cs-ticket-meta-value'>
                {viewingTicket?.created_at
                  ? moment(viewingTicket.created_at).format('dddd, DD MMM YYYY')
                  : '—'}
              </span>
            </div>
            <div className='cs-ticket-meta-row'>
              <span className='cs-ticket-meta-label'>Time</span>
              <span className='cs-ticket-meta-value'>
                {viewingTicket?.created_at ? moment(viewingTicket.created_at).format('HH:mm:ss') : '—'}
              </span>
            </div>
            <div className='cs-ticket-meta-row'>
              <span className='cs-ticket-meta-label'>Subject</span>
              <span className='cs-ticket-meta-value'>{viewingTicket?.subject}</span>
            </div>
            <div className='cs-ticket-meta-row'>
              <span className='cs-ticket-meta-label'>Status</span>
              <span className='cs-ticket-meta-value'>{viewingTicket?.status}</span>
            </div>
          </div>
          <div className='cs-ticket-query-section'>
            <h4 className='cs-ticket-query-title'>Description</h4>
            <div className='cs-ticket-query-text'>{viewingTicket?.description || viewingTicket?.message || 'No description provided.'}</div>
          </div>
        </div>
      </Modal>
    </div>
  )
}

const CustomerSupportDashboardWrapper: FC = () => {
  const { setRightTolbar } = useLayout()
  useEffect(() => { setRightTolbar('') }, [])
  return (
    <>
      <PageTitle breadcrumbs={[]}>Customer Support Dashboard</PageTitle>
      <CustomerSupportDashboard />
    </>
  )
}

export { CustomerSupportDashboardWrapper }
